import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@borderless/ui/lib/utils";

export const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border-border border shadow-[4px_4px_0_var(--primary-shadow)] transition-all duration-100 ease-in-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--primary-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none",
        secondary:
          "bg-secondary text-secondary-foreground border-border border shadow-[4px_4px_0_var(--secondary-shadow)] transition-all duration-100 ease-in-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--secondary-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none",
        destructive:
          "bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 border-border border text-white shadow-[4px_4px_0_var(--destructive-shadow)] transition-all duration-100 ease-in-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--destructive-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none",
        outline:
          "bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:border-input border",
        ghost: "hover:bg-accent hover:text-accent-foreground text-foreground",
        link: "text-foreground underline-offset-4 hover:underline",
        selectable:
          "bg-background text-secondary-foreground border-border data-[state=selected]:bg-secondary border shadow-[4px_4px_0_var(--secondary-shadow)] transition-all duration-100 ease-in-out hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_var(--secondary-shadow)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none data-[state=selected]:translate-x-[3.5px] data-[state=selected]:translate-y-[3.5px] data-[state=selected]:shadow-[0.5px_0.5px_0_var(--secondary-shadow)] data-[state=selected]:active:translate-x-[4px] data-[state=selected]:active:translate-y-[4px] data-[state=selected]:active:shadow-none",
        tertiary:
          "bg-tertiary text-tertiary-foreground border-border border shadow-[2px_2px_0_var(--tertiary-shadow)] transition-all duration-100 ease-in-out hover:translate-x-[0.5px] hover:translate-y-[0.5px] hover:shadow-[1.5px_1.5px_0_var(--tertiary-shadow)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>img]:px-3 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>img]:px-2.5 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>img]:px-4 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        iconTiny: "size-6",
        tiny: "h-6 gap-1.5 rounded-md px-2 has-[>img]:px-1.5 has-[>svg]:px-1.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}
